import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { LeadType, ObjectType, TaskForType } from '@econtractor/types';
import { useAuth } from 'context/AuthContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { canViewSales } from 'utils/helpers/access/hasUserAccess';
import { LeadSelected } from 'modules/lead';
import { ObjectSelected } from 'modules/objects';

export interface TaskForProps {
    taskFor: TaskForType;
    lead: LeadType | null;
    object: ObjectType | null;
    onTaskForChange: (taskFor: TaskForType) => void;
    onLeadChange: (lead: LeadType | null) => void;
    onObjectChange: (object: ObjectType | null) => void;
    disabled?: boolean;
    /** Заблокувати зміну типу (коли відкрито з заявки/об'єкта) */
    locked?: boolean;
}

const TaskFor = ({
    taskFor,
    lead,
    object,
    onTaskForChange,
    onLeadChange,
    onObjectChange,
    disabled = false,
    locked = false,
}: TaskForProps) => {
    const { t } = useTranslation(['Tasks']);
    const { auth } = useAuth();
    const params = useSettingsParams();
    const allowLeads = params.tasksModule && params.tasksForLeads;
    const allowObjects = params.tasksModule && params.tasksForObjects;
    const allowSales = allowObjects && canViewSales(auth?.user);
    const showObjectPicker =
        (taskFor === 'object' && allowObjects) ||
        ((taskFor === 'sale' || taskFor === 'service') && allowSales);

    const handleTypeChange = (next: TaskForType) => {
        if (disabled || locked) return;
        if (next === 'lead' && !allowLeads) return;
        if (next === 'object' && !allowObjects) return;
        if ((next === 'sale' || next === 'service') && !allowSales) return;
        onTaskForChange(next);
        if (next === 'personal') {
            onLeadChange(null);
            onObjectChange(null);
        } else if (next === 'lead') {
            onObjectChange(null);
        } else {
            onLeadChange(null);
            onObjectChange(null);
        }
    };

    const objectPickerTitle =
        taskFor === 'sale'
            ? t('fields.sale')
            : taskFor === 'service'
              ? t('fields.service')
              : undefined;

    return (
        <div className="task-for mb-3">
            {!locked && (
                <Form.Group className="mb-3">
                    <label className="form-label text-muted">{t('labelTaskFor')}</label>
                    <Form.Select
                        value={taskFor}
                        disabled={disabled}
                        onChange={(e) => handleTypeChange(e.target.value as TaskForType)}
                    >
                        <option value="personal">{t('taskFor.personal')}</option>
                        {allowLeads && <option value="lead">{t('taskFor.lead')}</option>}
                        {allowObjects && <option value="object">{t('taskFor.object')}</option>}
                        {allowSales && <option value="sale">{t('taskFor.sale')}</option>}
                        {allowSales && <option value="service">{t('taskFor.service')}</option>}
                    </Form.Select>
                </Form.Group>
            )}

            {taskFor === 'lead' && allowLeads && (
                <LeadSelected
                    value={lead}
                    onChange={onLeadChange}
                    disabled={disabled}
                    clearable={!locked}
                    maxHeight={200}
                />
            )}

            {showObjectPicker && (
                <ObjectSelected
                    key={taskFor}
                    value={object}
                    onChange={onObjectChange}
                    disabled={disabled}
                    clearable={!locked}
                    maxHeight={200}
                    onlySale={taskFor === 'sale'}
                    onlyService={taskFor === 'service'}
                    title={objectPickerTitle}
                />
            )}
        </div>
    );
};

export default TaskFor;
